import React, { Suspense, lazy } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { Layout } from './components/common/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import SeoHreflang from './components/common/SeoHreflang';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import { useI18n } from './contexts/I18nContext';
import { PREFIXED_LOCALES, localizePath } from './utils/localePaths';

const Loader = () => <div className="loading-screen"><div className="spinner" /></div>;

// Pages
const Home = lazy(() => import('./pages/Home'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const SocialSignup = lazy(() => import('./pages/SocialSignup'));
const KakaoChannel = lazy(() => import('./pages/KakaoChannel'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const LoginSuccess = lazy(() => import('./pages/LoginSuccess'));
const MyPage = lazy(() => import('./pages/MyPage'));
const Fortune = lazy(() => import('./pages/Fortune'));
const CharacterChat = lazy(() => import('./pages/CharacterChat'));
const SajuReport = lazy(() => import('./pages/SajuReport'));
const SajuGarden = lazy(() => import('./pages/SajuGarden'));
const SajuArtStudio = lazy(() => import('./pages/SajuArtStudio'));
const Tarot = lazy(() => import('./pages/Tarot'));
const TarotResult = lazy(() => import('./pages/TarotResult'));
const Gunghap = lazy(() => import('./pages/Gunghap'));
const SajuProView = lazy(() => import('./pages/SajuProView'));
const Board = lazy(() => import('./pages/Board'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const PostWrite = lazy(() => import('./pages/PostWrite'));
const PostEdit = lazy(() => import('./pages/PostEdit'));
const Charge = lazy(() => import('./pages/Charge'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentFail = lazy(() => import('./pages/PaymentFail'));
const Search = lazy(() => import('./pages/Search'));
const ManseryeokHistory = lazy(() => import('./pages/ManseryeokHistory'));
const UsageHistory = lazy(() => import('./pages/UsageHistory'));
const UsageHistoryDetail = lazy(() => import('./pages/UsageHistoryDetail'));
const ManseryeokAI = lazy(() => import('./pages/ManseryeokAI'));
const Encyclopedia = lazy(() => import('./pages/Encyclopedia'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PsychTest = lazy(() => import('./pages/PsychTest'));
const PsychTestTake = lazy(() => import('./pages/PsychTestTake'));
const Result = lazy(() => import('./pages/Result'));
const Gallery = lazy(() => import('./pages/Gallery'));
const GalleryDetail = lazy(() => import('./pages/GalleryDetail'));
const GalleryWrite = lazy(() => import('./pages/GalleryWrite'));
const GalleryEdit = lazy(() => import('./pages/GalleryEdit'));

const Notifications = lazy(() => import('./pages/Notifications'));
const AdminERP = lazy(() => import('./pages/AdminERP'));
const MyeongriPortal = lazy(() => import('./pages/MyeongriPortal'));
const DeveloperPortal = lazy(() => import('./pages/DeveloperPortal'));
const Franchise = lazy(() => import('./pages/Franchise'));
const SajuFarm = lazy(() => import('./pages/SajuFarm'));
const AsciiTarot = lazy(() => import('./pages/AsciiTarot'));
const AsciiTarotGallery = lazy(() => import('./pages/AsciiTarotGallery'));
const Celebrities = lazy(() => import('./pages/Celebrities'));
const CelebrityDetail = lazy(() => import('./pages/CelebrityDetail'));
const Astrology = lazy(() => import('./pages/Astrology'));
const SajuMap = lazy(() => import('./pages/SajuMap'));
const LiveLounge = lazy(() => import('./pages/LiveLounge'));
const LiveChatRoomPage = lazy(() => import('./pages/LiveChatRoomPage'));
const VoiceRoomPage = lazy(() => import('./pages/VoiceRoomPage'));
const RadioStudioPage = lazy(() => import('./pages/RadioStudioPage'));
const RadioPlayerPage = lazy(() => import('./pages/RadioPlayerPage'));

function LocaleNavigate({ to }) {
  const { lang } = useI18n();
  return <Navigate to={localizePath(to, lang)} replace />;
}

const sharedRoutes = () => (
  <>
    <Route index element={<Home />} />
    <Route path="login" element={<Login />} />
    <Route path="register" element={<Register />} />
    <Route path="social-signup" element={<SocialSignup />} />
    <Route path="kakao-channel" element={<KakaoChannel />} />
    <Route path="forgot-password" element={<ForgotPassword />} />
    <Route path="reset-password" element={<ResetPassword />} />
    <Route path="login-success" element={<LoginSuccess />} />
    <Route path="mypage" element={<MyPage />} />
    <Route path="fortune" element={<Fortune />} />
    <Route path="saju-garden" element={<SajuGarden />} />
    <Route path="saju-art" element={<LocaleNavigate to="/saju-garden" />} />
    <Route path="saju-webtoon" element={<SajuArtStudio product="webtoon" />} />
    <Route path="life-scroll" element={<SajuArtStudio product="life-scroll" />} />
    <Route path="guardian-art" element={<SajuArtStudio product="guardian" />} />
    <Route path="character-chat/:persona" element={<ProtectedRoute><CharacterChat /></ProtectedRoute>} />
    <Route path="saju-report" element={<ProtectedRoute><SajuReport /></ProtectedRoute>} />
    <Route path="saju" element={<LocaleNavigate to="/fortune" />} />
    <Route path="result" element={<Result />} />
    <Route path="tarot" element={<Tarot />} />
    <Route path="tarot-result" element={<TarotResult />} />
    <Route path="gunghap" element={<Gunghap />} />
    <Route path="saju-pro" element={<SajuProView />} />
    <Route path="board" element={<Board />} />
    <Route path="board/:id" element={<PostDetail />} />
    <Route path="board/write" element={<PostWrite />} />
    <Route path="board/edit/:id" element={<PostEdit />} />
    <Route path="charge" element={<Charge />} />
    <Route path="payment-success" element={<PaymentSuccess />} />
    <Route path="payment/success" element={<PaymentSuccess />} />
    <Route path="payment-fail" element={<PaymentFail />} />
    <Route path="payment/fail" element={<PaymentFail />} />
    <Route path="search" element={<Search />} />
    <Route path="manseryeok-ai" element={<ManseryeokAI />} />
    <Route path="manseryeok-history" element={<ManseryeokHistory />} />
    <Route path="usage-history" element={<UsageHistory />} />
    <Route path="usage-history/:type/:id" element={<UsageHistoryDetail />} />
    <Route path="saju-history" element={<LocaleNavigate to="/usage-history" />} />
    <Route path="encyclopedia" element={<Encyclopedia />} />
    <Route path="admin" element={<AdminERP />} />
    <Route path="admin-dashboard" element={<AdminERP />} />
    <Route path="privacy" element={<Privacy />} />
    <Route path="privacy-policy" element={<PrivacyPolicy />} />
    <Route path="terms" element={<Terms />} />
    <Route path="terms-of-service" element={<TermsOfService />} />
    <Route path="refund-policy" element={<RefundPolicy />} />
    <Route path="psychtest" element={<PsychTest />} />
    <Route path="psychtest/:testId" element={<PsychTestTake />} />
    <Route path="gallery" element={<Gallery />} />
    <Route path="gallery/:id" element={<GalleryDetail />} />
    <Route path="gallery/write" element={<GalleryWrite />} />
    <Route path="gallery/edit/:id" element={<GalleryEdit />} />
    <Route path="notifications" element={<Notifications />} />
    <Route path="admin-erp" element={<AdminERP />} />
    <Route path="myeongri" element={<MyeongriPortal />} />
    <Route path="calendar" element={<LocaleNavigate to="/saju-pro" />} />
    <Route path="developer" element={<DeveloperPortal />} />
    <Route path="franchise" element={<Franchise />} />
    <Route path="franchise-mall" element={<Franchise />} />
    <Route path="saju-mall" element={<Franchise />} />
    <Route path="saju-farm" element={<SajuFarm />} />
    <Route path="ascii-tarot" element={<AsciiTarot />} />
    <Route path="ascii-tarot-gallery" element={<AsciiTarotGallery />} />
    <Route path="celebrities" element={<Celebrities />} />
    <Route path="celebrities/:slug" element={<CelebrityDetail />} />
    <Route path="live" element={<LiveLounge />} />
    <Route path="live/chat/:roomId" element={<LiveChatRoomPage />} />
    <Route path="live/chat/:chatRoomId/call" element={<VoiceRoomPage />} />
    <Route path="live/voice/:roomId" element={<VoiceRoomPage />} />
    <Route path="live/radio/studio" element={<RadioStudioPage />} />
    <Route path="live/radio/:idOrRoom" element={<RadioPlayerPage />} />
    <Route path="astrology" element={<Astrology />} />
    <Route path="astrology/r/:shareId" element={<Astrology />} />
    <Route path="horoscope" element={<LocaleNavigate to="/astrology" />} />
    <Route path="saju-map" element={<SajuMap />} />
    <Route path="jeomjip-map" element={<LocaleNavigate to="/saju-map" />} />
  </>
);

function App() {
  return (
    <Suspense fallback={<Loader />}>
      <SeoHreflang />
      <PWAInstallPrompt />
      <Routes>
        <Route element={<Layout />}>
          {sharedRoutes()}
          {PREFIXED_LOCALES.map(locale => (
            <Route key={locale} path={locale}>
              {sharedRoutes()}
            </Route>
          ))}
          <Route path="*" element={<LocaleNavigate to="/" />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
